import type { ReactNode } from 'react'
import { cva } from 'class-variance-authority'
import { ItIcon } from '../primitives/it-icon'
import type { ItIconName } from '../primitives/it-icon'
import { cn } from '../primitives/util'

/**
 * Status pill for tables, headers, list rows. Tones cover the semantic
 * palette (neutral/success/warning/danger/info/brand), the severity ramp
 * (critical/high/medium/low — identical hues to charts and graph nodes),
 * and cloud providers (aws/gcp/azure). Always renders a text label —
 * severity is never color alone.
 *
 * shadcn/cva architecture; tone colors come straight from the design tokens.
 */

export type BadgeTone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'brand'
  | 'tertiary'
  | 'critical'
  | 'high'
  | 'medium'
  | 'low'
  | 'aws'
  | 'gcp'
  | 'azure'
  | 'brex'
  | 'googleworkspace'

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11.5px] font-medium leading-normal',
  {
    variants: {
      tone: {
        neutral: 'bg-[var(--bg-sunk)] text-[var(--text-mute)]',
        brand: 'bg-[var(--brand-tint)] text-[var(--brand-tint-fg)]',
        tertiary: 'bg-[var(--tertiary-tint)] text-[var(--tertiary-tint-fg)]',
        success: 'bg-[var(--ok-100)] text-[var(--ok-700)]',
        warning: 'bg-[var(--warn-100)] text-[var(--warn-700)]',
        danger: 'bg-[var(--err-100)] text-[var(--err-700)]',
        info: 'bg-[var(--info-100)] text-[var(--info-700)]',
        critical: 'bg-[var(--sev-critical-bg)] text-[var(--sev-critical-fg)]',
        high: 'bg-[var(--sev-high-bg)] text-[var(--sev-high-fg)]',
        medium: 'bg-[var(--sev-medium-bg)] text-[var(--sev-medium-fg)]',
        low: 'bg-[var(--sev-low-bg)] text-[var(--sev-low-fg)]',
        aws: 'bg-[var(--aws-bg)] text-[var(--aws-fg)]',
        gcp: 'bg-[var(--gcp-bg)] text-[var(--gcp-fg)]',
        azure: 'bg-[var(--azure-bg)] text-[var(--azure-fg)]',
        // Stub provider — falls back to neutral tokens until brand tokens land.
        brex: 'bg-[var(--brex-bg,var(--bg-sunk))] text-[var(--brex-fg,var(--text-mute))]',
        // No distinct brand tokens yet — reuse the GCP tint.
        googleworkspace: 'bg-[var(--gcp-bg)] text-[var(--gcp-fg)]',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
)

export interface BadgeProps {
  tone?: BadgeTone
  dot?: boolean
  icon?: ItIconName
  className?: string
  children?: ReactNode
}

export function Badge({ tone = 'neutral', dot = false, icon, className, children }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ tone }), className)}>
      {dot && <span className="size-1.5 flex-none rounded-full bg-current" aria-hidden="true" />}
      {icon && <ItIcon name={icon} size={12} />}
      {children}
    </span>
  )
}
